import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { LOGO_URL } from "@/lib/brand";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

const errToString = (detail) => {
  if (!detail) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return JSON.stringify(detail);
};

const RegisterPage = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handle = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (!executeRecaptcha) { toast.error("reCAPTCHA not ready yet. Try again in a moment."); return; }
    setLoading(true);
    try {
      const captchaToken = await executeRecaptcha("register");
      await register(name, email, password, captchaToken);
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(errToString(e?.response?.data?.detail) || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center justify-center px-4 py-10" data-testid="register-page">
      <div className="w-full max-w-md pav-card">
        <div className="flex items-center gap-3 mb-6">
          <img src={LOGO_URL} alt="" className="h-10 w-10 rounded-lg" />
          <div>
            <h1 className="font-display text-2xl font-extrabold text-[#0B1E3F]">Create your account</h1>
            <p className="text-sm text-neutral-500">Track orders, re-print designs faster</p>
          </div>
        </div>
        <form onSubmit={handle} className="space-y-3" data-testid="register-recaptcha">
          <input className="pav-input" placeholder="Full name"
            data-testid="register-name-input"
            value={name} onChange={(e) => setName(e.target.value)} required />
          <input className="pav-input" type="email" placeholder="Email"
            data-testid="register-email-input"
            value={email} onChange={(e) => setEmail(e.target.value)} required />
          <input className="pav-input" type="password" placeholder="Password (min 6 chars)"
            data-testid="register-password-input"
            value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} />

          {/* Visible reCAPTCHA disclosure (v3 invisible — badge hidden, this serves as Google's required disclosure) */}
          <div className="flex items-center gap-2.5 rounded-xl border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5"
            data-testid="register-recaptcha-banner">
            <img alt="reCAPTCHA"
              src="https://www.gstatic.com/recaptcha/api2/logo_48.png"
              className="h-7 w-7 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[12px] font-semibold text-[#0B1E3F]">Protected by reCAPTCHA</p>
              <p className="text-[10px] text-neutral-500 leading-tight">
                Google&nbsp;
                <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline">Privacy</a>
                &nbsp;·&nbsp;
                <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline">Terms</a>
              </p>
            </div>
            <span className="h-2 w-2 rounded-full bg-green-500" title="Active" />
          </div>

          <button type="submit" disabled={loading}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
            data-testid="register-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Create account
          </button>
        </form>
        <p className="text-sm text-neutral-600 mt-6 text-center">
          Already a customer? <Link to="/login" className="text-[#0B1E3F] font-semibold hover:underline" data-testid="register-login-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
